import { handleGetConfigBranches, handleGetConfigFile } from '@/playground/handlers/gameConfigHandlers.js';
import { expandOccurrences, scheduleFrom, scheduleStartInstant } from '../model/schedule.js';
import { mockTestRun, proposeConfigEdit } from '../model/taskResults.js';
import { diffLeaves, parseObject, writePath } from '../model/contract.js';
import { defaultsBlockers, linkedIds } from '../model/eventModel.js';
import { draftKnowledgeTurn } from '../model/knowledgeCapture.js';
import { EVENT_SYSTEM } from '../model/vocab.js';
import { DAY, METRICS, iso, newEvent, scoreOutcome, seed } from './playgroundSeed.js';

const STORE_KEY = 'pg_live_events_v4';
const blobs = new Map();
// A knowledge file's data URI stays in memory, out of sessionStorage, so a few uploads can't fill its quota.
const fileData = new Map();

export const isPlayground = () => {
    try {
        return sessionStorage.getItem('playgroundMode') === 'true';
    } catch {
        return false;
    }
};

const load = () => {
    try {
        const raw = sessionStorage.getItem(STORE_KEY);
        if (raw) return JSON.parse(raw);
    } catch {
        return seed();
    }
    const fresh = seed();
    sessionStorage.setItem(STORE_KEY, JSON.stringify(fresh));
    return fresh;
};

const save = (db) => sessionStorage.setItem(STORE_KEY, JSON.stringify(db));

const nextId = (db, prefix) => {
    db.seq += 1;
    return `pg-${prefix}-${db.seq}`;
};

const notFound = (what) => {
    const error = new Error(`${what} not found`);
    error.response = { status: 404, data: { message: `${what} not found`, errorCode: 'ENTITY_NOT_FOUND' } };
    return error;
};

const refused = (message) => {
    const error = new Error(message);
    error.response = { status: 400, data: { message } };
    return error;
};

const patch = (row, data) => {
    Object.entries(data || {}).forEach(([k, v]) => { if (v !== null && v !== undefined) row[k] = v; });
    row.updatedAt = iso(Date.now());
    return row;
};

const fileRecord = (db, form, extra) => {
    const file = form.get('file');
    const id = nextId(db, 'doc');
    blobs.set(id, URL.createObjectURL(file));
    return { id, name: file.name, mimeType: file.type || 'application/octet-stream', size: file.size, sourceUrl: null, createdAt: iso(Date.now()), ...extra };
};

const upcoming = (db, horizonDays) => {
    const now = Date.now();
    const until = now + horizonDays * DAY;
    const events = [];
    db.placements.forEach((p) => {
        const ev = db.events.find((e) => e.id === p.beatId);
        if (!ev) return;
        const s = scheduleFrom(ev);
        const anchor = scheduleStartInstant(s, 'UTC');
        if (anchor == null) return;
        expandOccurrences(s, anchor, 'UTC', Math.ceil((until - anchor) / DAY) + 1)
            .filter((ms) => ms >= now && ms <= until)
            .forEach((ms) => events.push({ timelineItemId: p.id, beatId: ev.id, title: ev.name, occurrenceStart: iso(ms) }));
    });
    events.sort((a, b) => a.occurrenceStart.localeCompare(b.occurrenceStart));
    return { upcoming: { timezone: 'UTC', horizonDays, returnedCount: events.length, truncated: false, days: [{ day: 'all', events }] } };
};

const eventOf = (db, id) => {
    const row = db.events.find((e) => e.id === id);
    if (!row) throw notFound('Live event');
    return row;
};

const touched = (row) => {
    row.updatedAt = iso(Date.now());
    return { liveEvent: row };
};

// ── The event's full copy of each linked config, as GET and PUT /configs keep it ──

const pretty = (value) => JSON.stringify(value, null, 2);
const objectOf = (text) => parseObject(String(text ?? '')) || {};
const lineage = (b) => b?.versionInfo?.parentEntityId || b?.id || null;
// As configs' isBaseBranch, kept here: branchModel's imports reach the Redux store, which would load with this module.
const isBase = (b) => !!(b?.isBase ?? b?.base);

// The branch a link copies: the version it names, else the file's base (read from the playground's Configs store).
const sourceOf = (link) => {
    const all = handleGetConfigBranches({}, [null, link.configFileId]);
    return (link.parentBranchId && all.find((b) => lineage(b) === link.parentBranchId)) || all.find(isBase) || null;
};

// What was saved as the copy, else the source with the seed's per-value changes laid over it.
const copyText = (row, link, source) => {
    const saved = row.copies?.[link.configFileId];
    if (saved != null) return saved;
    const content = objectOf(source?.content);
    (row.changes || []).filter((c) => c.configFileId === link.configFileId).forEach((c) => writePath(content, c.path, c.defaultValue));
    return pretty(content);
};

const linkedConfig = (row, link) => {
    const source = sourceOf(link);
    const content = copyText(row, link, source);
    let configName = link.configFileId;
    try {
        configName = handleGetConfigFile({}, [null, link.configFileId])?.name || configName;
    } catch { /* a file that is gone keeps its id */ }
    return {
        configFileId: link.configFileId,
        configName,
        sourceBranchId: lineage(source),
        sourceName: source?.name || null,
        sourceContent: source?.content ?? null,
        // No date is ever staged from a copy here, so the page never needs the save review.
        templateBranchId: null,
        content,
        changedPaths: Object.keys(diffLeaves(objectOf(source?.content), objectOf(content)).changed),
        reviewNote: null,
    };
};

const requireMetric = (id) => {
    if (!METRICS.some((m) => m.metricId === id)) throw refused(`No metric ${id} in the metric catalog.`);
};

const checkTarget = (input) => {
    if (!input?.metricId) return null;
    requireMetric(input.metricId);
    const direction = input.direction || 'UP';
    const mode = input.mode || 'PERCENT';
    if (direction === 'HOLD') {
        return { metricId: input.metricId, direction, mode: 'PERCENT', percent: null, value: null, holdTolerancePct: input.holdTolerancePct ?? 2 };
    }
    if (mode === 'PERCENT' && !(input.percent > 0)) throw refused('Say how much the metric should move, as a positive percentage.');
    if (mode === 'ABSOLUTE' && input.value == null) throw refused('Say the level the metric should reach.');
    return {
        metricId: input.metricId, direction, mode, percent: mode === 'PERCENT' ? input.percent : null,
        value: mode === 'ABSOLUTE' ? input.value : null, holdTolerancePct: null,
    };
};

// As the server builds it: a date carries its own run record's notes, and only runs logged by hand are LOGGED.
const historyOf = (db, eventId) => {
    const runs = db.runs.filter((r) => r.liveEventId === eventId);
    const logOf = new Map(runs.filter((r) => r.instanceId).map((r) => [r.instanceId, r]));
    return [
        ...db.instances.filter((i) => i.beatId === eventId).map((i) => {
            const log = logOf.get(i.id);
            return {
                kind: 'DATE', instanceId: i.id, runId: log?.id || null, title: log?.title || null, at: i.occStart, status: i.status,
                windowStartsAt: i.occStart, windowEndsAt: i.windowEndsAt, publishedAt: i.publishedAt, skipReason: i.skipReason,
                shipped: i.shipped, outcome: i.outcome, notes: log?.notes || null, results: log?.results || null,
            };
        }),
        ...runs.filter((r) => !r.instanceId).map((r) => ({
            kind: 'LOGGED', runId: r.id, title: r.title, at: r.ranAt, outcome: r.outcome || null, notes: r.notes, results: r.results,
        })),
    ].sort((a, b) => String(b.at).localeCompare(String(a.at)));
};

const deleteEvent = (db, id) => {
    const lists = db.taskLists.filter((l) => l.beatId === id).map((l) => l.id);
    const runs = db.runs.filter((r) => r.liveEventId === id).map((r) => r.id);
    db.taskLists = db.taskLists.filter((l) => l.beatId !== id);
    db.runs = db.runs.filter((r) => r.liveEventId !== id);
    db.runArtifacts = db.runArtifacts.filter((a) => !runs.includes(a.liveEventRunId));
    // The event's own files are archived; the server keeps them out of every list from then on.
    db.documents.filter((d) => d.system === EVENT_SYSTEM && d.systemId === id).forEach((d) => {
        d.status = 'ARCHIVED';
        d.indexedAt = null;
    });
    db.placements = db.placements.filter((p) => p.beatId !== id);
    db.instances = db.instances.filter((i) => i.beatId !== id);
    lists.forEach((l) => { delete db.testRuns[l]; });
    // Its snippets stay, as knowledge of the whole game.
    db.snippets.forEach((sn) => {
        if (sn.system !== EVENT_SYSTEM || sn.systemId !== id) return;
        sn.system = 'GAME_KNOWLEDGE';
        sn.systemId = null;
    });
    db.events = db.events.filter((e) => e.id !== id);
};

// As the server filters files and snippets: one system, and with an id that record's own plus the system's shared
// ones. A row stored without a system reads as Game Knowledge.
const inSystem = (row, params) => !params?.system
    || ((row.system || 'GAME_KNOWLEDGE') === params.system && (!params.systemId || !row.systemId || row.systemId === params.systemId));

const documentOf = (db, id) => {
    const row = db.documents.find((d) => d.id === id);
    if (!row) throw notFound('Knowledge entry');
    return row;
};

const withFileData = (row) => ({ ...row, payload: { ...row.payload, dataUri: fileData.get(row.id) } });

const listOf = (db, id) => {
    const list = db.taskLists.find((l) => l.id === id);
    if (!list) throw notFound('Task list');
    return list;
};

const ROUTES = [
    ['get', /^\/live-events$/, (db) => ({ liveEvents: [...db.events].sort((a, b) => a.sortOrder - b.sortOrder) })],
    ['post', /^\/live-events$/, (db, m, { data }) => {
        // As the server creates one: from the name and the fields sent, with no schedule until one is set.
        const row = {
            ...newEvent(nextId(db, 'le'), { tzMode: null, timezone: null, recurrence: null }),
            category: 'LIVEOPS', owner: 'GEOPLAY', sortOrder: db.events.length, createdAt: iso(Date.now()),
        };
        db.events.push(patch(row, data));
        return { liveEvent: row };
    }],
    ['get', /^\/live-events\/([^/]+)$/, (db, m) => {
        const row = db.events.find((e) => e.id === m[1]);
        if (!row) throw notFound('Live event');
        return { liveEvent: row };
    }],
    ['put', /^\/live-events\/([^/]+)$/, (db, m, { data }) => {
        const row = db.events.find((e) => e.id === m[1]);
        if (!row) throw notFound('Live event');
        return { liveEvent: patch(row, data) };
    }],
    ['delete', /^\/live-events\/([^/]+)$/, (db, m) => {
        eventOf(db, m[1]);
        deleteEvent(db, m[1]);
        return { success: true };
    }],
    ['put', /^\/live-events\/([^/]+)\/config-links$/, (db, m, { data }) => {
        const row = eventOf(db, m[1]);
        const before = new Map(row.configLinks.map((l) => [l.configFileId, l]));
        row.configLinks = (data?.configLinks || []).filter((l) => l?.configFileId)
            .map((l) => ({ configFileId: l.configFileId, parentBranchId: l.parentBranchId || null }));
        const kept = linkedIds(row);
        row.changes = row.changes.filter((c) => kept.includes(c.configFileId));
        // A copy follows its link: an unlinked file's goes, and a new version takes the copy's own values with it.
        row.copies = Object.fromEntries(row.configLinks.flatMap((link) => {
            const old = before.get(link.configFileId);
            const saved = row.copies?.[link.configFileId];
            if (saved == null || !old) return [];
            if ((old.parentBranchId || null) === link.parentBranchId) return [[link.configFileId, saved]];
            const moved = objectOf(sourceOf(link)?.content);
            Object.entries(diffLeaves(objectOf(sourceOf(old)?.content), objectOf(saved)).changed).forEach(([path, value]) => writePath(moved, path, value));
            return [[link.configFileId, pretty(moved)]];
        }));
        return touched(row);
    }],
    ['put', /^\/live-events\/([^/]+)\/audience$/, (db, m, { data }) => {
        const row = eventOf(db, m[1]);
        row.segmentIds = [...new Set(data?.segmentIds || [])];
        row.effectivePriorityTier = row.priorityTier || (row.segmentIds.length ? 'FEATURED' : 'STANDARD');
        return touched(row);
    }],
    // The server also asks for the game's owner before CRITICAL; the playground has nobody else.
    ['put', /^\/live-events\/([^/]+)\/priority-tier$/, (db, m, { data }) => {
        const row = eventOf(db, m[1]);
        row.priorityTier = data?.priorityTier || null;
        row.effectivePriorityTier = row.priorityTier || ((row.segmentIds || []).length ? 'FEATURED' : 'STANDARD');
        return touched(row);
    }],
    ['put', /^\/live-events\/([^/]+)\/audience-hold$/, (db, m, { data }) => {
        const row = eventOf(db, m[1]);
        if (data?.audienceHold) row.audienceHold = data.audienceHold;
        return touched(row);
    }],
    // Refused with every blocker named, as the server does; the lead time is replaced, so a missing one clears it.
    ['put', /^\/live-events\/([^/]+)\/run-mode$/, (db, m, { data }) => {
        const row = eventOf(db, m[1]);
        const mode = data?.runMode || row.runMode || 'REVIEW';
        if (mode === 'DEFAULTS') {
            const blockers = defaultsBlockers(row);
            if (blockers.length) throw refused(`This event cannot ship on its defaults yet: ${blockers.join(' ')}`);
        }
        row.runMode = mode;
        row.reviewLeadHours = data?.reviewLeadHours ?? null;
        return touched(row);
    }],
    ['get', /^\/live-events\/([^/]+)\/defaults-blockers$/, (db, m) => ({ blockers: defaultsBlockers(eventOf(db, m[1])) })],
    ['put', /^\/live-events\/([^/]+)\/target$/, (db, m, { data }) => {
        const row = eventOf(db, m[1]);
        row.target = checkTarget(data);
        return touched(row);
    }],
    ['post', /^\/live-events\/([^/]+)\/guardrails$/, (db, m, { data }) => {
        const row = eventOf(db, m[1]);
        if (!data?.metricId) throw refused('A guardrail needs a metric.');
        requireMetric(data.metricId);
        if (row.guardrails.length >= 4) throw refused('An event can have at most 4 guardrails.');
        if (row.guardrails.some((g) => g.metricId === data.metricId)) throw refused(`There is already a guardrail on ${data.metricId}.`);
        row.guardrails.push({ id: nextId(db, 'gr'), metricId: data.metricId, direction: data.direction || 'MUST_NOT_FALL', tolerancePct: data.tolerancePct ?? null });
        return touched(row);
    }],
    ['delete', /^\/live-events\/([^/]+)\/guardrails\/([^/]+)$/, (db, m) => {
        const row = eventOf(db, m[1]);
        row.guardrails = row.guardrails.filter((g) => g.id !== m[2]);
        return touched(row);
    }],
    ['get', /^\/live-events\/([^/]+)\/configs$/, (db, m) => {
        const row = eventOf(db, m[1]);
        return { configs: row.configLinks.map((link) => linkedConfig(row, link)) };
    }],
    ['put', /^\/live-events\/([^/]+)\/configs\/([^/]+)$/, (db, m, { data }) => {
        const row = eventOf(db, m[1]);
        const link = row.configLinks.find((l) => l.configFileId === m[2]);
        if (!link) throw refused('That config is not linked to the event.');
        if (data?.content == null) throw refused("Send the config's full content.");
        const next = parseObject(String(data.content));
        if (!next) throw refused("The config's content has to be a JSON object.");
        const { removed } = diffLeaves(objectOf(sourceOf(link)?.content), next);
        if (removed.length) throw refused(`An event's copy can't remove keys its source has: ${removed.slice(0, 5).join(', ')}.`);
        row.copies = { ...(row.copies || {}), [link.configFileId]: pretty(next) };
        row.changes = row.changes.filter((c) => c.configFileId !== link.configFileId);
        touched(row);
        return { configs: [linkedConfig(row, link)] };
    }],
    ['get', /^\/live-events\/([^/]+)\/history$/, (db, m) => ({ history: historyOf(db, eventOf(db, m[1]).id) })],
    ['post', /^\/live-event-instances\/([^/]+)\/outcome$/, (db, m, { data }) => {
        const inst = db.instances.find((i) => i.id === m[1]);
        if (!inst) throw notFound('Date');
        if (!inst.publishedAt) throw refused('Only a date that published can be measured.');
        if (new Date(inst.windowEndsAt).getTime() > Date.now()) throw refused("This date's window has not closed yet; measure it once it ends.");
        inst.outcome = scoreOutcome(eventOf(db, inst.beatId), data, inst.outcome || null);
        return { liveEventInstance: inst };
    }],
    // The playground has no warehouse, so it answers the way the server does for a date it can't read.
    ['post', /^\/live-event-instances\/([^/]+)\/outcome\/compute$/, (db, m) => {
        if (!db.instances.some((i) => i.id === m[1])) throw notFound('Date');
        throw refused('The playground has no game data to measure from; record the result by hand.');
    }],
    ['get', /^\/game\/metric-catalog\/active$/, () => ({ metricCatalogs: METRICS })],
    // Knowledge files, as the Game Knowledge API sends them: a bare array, latest version only. An event's upload is
    // published at once, so it's indexed from the start.
    ['get', /^\/game-knowledge$/, (db, m, { params }) => db.documents.filter((d) => inSystem(d, params)).map(withFileData).reverse()],
    ['post', /^\/game-knowledge$/, (db, m, { data }) => {
        const { dataUri, ...payload } = data?.payload || {};
        const id = nextId(db, 'doc');
        fileData.set(id, dataUri);
        const row = {
            id, parentEntityId: id, name: data.name, category: data.category, storageMode: data.storageMode, status: 'ACTIVE',
            system: data.system || 'GAME_KNOWLEDGE', systemId: data.systemId || null, payload,
            indexedAt: data.system === EVENT_SYSTEM ? iso(Date.now()) : null, createdAt: iso(Date.now()),
        };
        db.documents.push(row);
        return withFileData(row);
    }],
    ['post', /^\/game-knowledge\/([^/]+)\/archive$/, (db, m) => {
        const row = documentOf(db, m[1]);
        row.status = 'ARCHIVED';
        row.indexedAt = null;
        return withFileData(row);
    }],
    ['delete', /^\/game-knowledge\/([^/]+)$/, (db, m) => {
        if (documentOf(db, m[1]).status !== 'ARCHIVED') throw refused('Entry must be archived before hard delete.');
        db.documents = db.documents.filter((d) => d.id !== m[1]);
        fileData.delete(m[1]);
        return { parentEntityId: m[1], deletedCount: 1 };
    }],
    ['get', /^\/live-events\/([^/]+)\/runs$/, (db, m) => ({
        liveEventRuns: db.runs.filter((r) => r.liveEventId === m[1]).sort((a, b) => b.ranAt.localeCompare(a.ranAt)),
    })],
    ['post', /^\/live-events\/([^/]+)\/runs$/, (db, m, { data }) => {
        const row = patch({ id: nextId(db, 'run'), liveEventId: m[1], createdAt: iso(Date.now()) }, data);
        db.runs.push(row);
        return { liveEventRun: row };
    }],
    ['put', /^\/live-events\/([^/]+)\/runs\/([^/]+)$/, (db, m, { data }) => {
        const row = db.runs.find((r) => r.id === m[2]);
        if (!row) throw notFound('Run');
        return { liveEventRun: patch(row, data) };
    }],
    ['delete', /^\/live-events\/([^/]+)\/runs\/([^/]+)$/, (db, m) => {
        db.runs = db.runs.filter((r) => r.id !== m[2]);
        db.runArtifacts = db.runArtifacts.filter((a) => a.liveEventRunId !== m[2]);
        return { success: true };
    }],
    ['get', /^\/live-events\/([^/]+)\/runs\/([^/]+)\/artifacts$/, (db, m) => ({
        liveEventRunArtifacts: db.runArtifacts.filter((a) => a.liveEventRunId === m[2]),
    })],
    ['post', /^\/live-events\/([^/]+)\/runs\/([^/]+)\/artifacts\/config$/, (db, m, { data }) => {
        const row = {
            id: nextId(db, 'ra'), liveEventRunId: m[2], role: data?.role || 'INPUT', name: data?.configFileId, size: 0, mimeType: null,
            sourceUrl: null, configFileId: data?.configFileId, configBranchId: data?.configBranchId || null,
            configVersion: data?.configVersion ?? null, createdAt: iso(Date.now()),
        };
        db.runArtifacts.push(row);
        return { liveEventRunArtifact: row };
    }],
    ['post', /^\/live-events\/([^/]+)\/runs\/([^/]+)\/artifacts\/link$/, (db, m, { data }) => {
        const row = { id: nextId(db, 'ra'), liveEventRunId: m[2], size: 0, mimeType: null, createdAt: iso(Date.now()), ...data };
        db.runArtifacts.push(row);
        return { liveEventRunArtifact: row };
    }],
    ['post', /^\/live-events\/([^/]+)\/runs\/([^/]+)\/artifacts$/, (db, m, { form, params }) => {
        const row = fileRecord(db, form, { liveEventRunId: m[2], role: params?.role || 'INPUT' });
        db.runArtifacts.push(row);
        return { liveEventRunArtifact: row };
    }],
    ['delete', /^\/live-events\/([^/]+)\/runs\/([^/]+)\/artifacts\/([^/]+)$/, (db, m) => {
        db.runArtifacts = db.runArtifacts.filter((a) => a.id !== m[3]);
        return { success: true };
    }],
    ['get', /^\/live-event-task-lists$/, (db, m, { params }) => ({
        liveEventTaskLists: db.taskLists.filter((l) => l.beatId === params?.beatId)
            .sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || a.sortOrder - b.sortOrder),
    })],
    ['post', /^\/live-event-task-lists$/, (db, m, { data }) => {
        const siblings = db.taskLists.filter((l) => l.beatId === data.beatId);
        const row = {
            id: nextId(db, 'list'), beatId: data.beatId, name: data.name, sortOrder: siblings.length, createdAt: iso(Date.now()),
            isDefault: siblings.length === 0 || !!data.isDefault,
            items: (data.items || []).map((t, i) => ({ id: nextId(db, 'task'), sortOrder: i, required: true, links: [], ...t })),
        };
        if (row.isDefault) siblings.forEach((l) => { l.isDefault = false; });
        db.taskLists.push(row);
        return { liveEventTaskList: row };
    }],
    ['put', /^\/live-event-task-lists\/([^/]+)$/, (db, m, { data }) => {
        const row = listOf(db, m[1]);
        if (data?.isDefault) db.taskLists.filter((l) => l.beatId === row.beatId).forEach((l) => { l.isDefault = false; });
        return { liveEventTaskList: patch(row, { name: data?.name, isDefault: data?.isDefault, sortOrder: data?.sortOrder }) };
    }],
    ['delete', /^\/live-event-task-lists\/([^/]+)$/, (db, m) => {
        const row = listOf(db, m[1]);
        db.taskLists = db.taskLists.filter((l) => l.id !== m[1]);
        const rest = db.taskLists.filter((l) => l.beatId === row.beatId);
        if (row.isDefault && rest.length && !rest.some((l) => l.isDefault)) rest[0].isDefault = true;
        return { success: true };
    }],
    ['post', /^\/live-event-task-lists\/([^/]+)\/tasks$/, (db, m, { data }) => {
        const row = listOf(db, m[1]);
        row.items.push({ id: nextId(db, 'task'), sortOrder: row.items.length, required: true, links: [], description: null, ...data });
        return { liveEventTaskList: patch(row, {}) };
    }],
    ['put', /^\/live-event-task-lists\/([^/]+)\/tasks\/([^/]+)$/, (db, m, { data }) => {
        const row = listOf(db, m[1]);
        const task = row.items.find((t) => t.id === m[2]);
        if (!task) throw notFound('Task');
        patch(task, data);
        return { liveEventTaskList: patch(row, {}) };
    }],
    ['delete', /^\/live-event-task-lists\/([^/]+)\/tasks\/([^/]+)$/, (db, m) => {
        const row = listOf(db, m[1]);
        row.items = row.items.filter((t) => t.id !== m[2]);
        return { liveEventTaskList: patch(row, {}) };
    }],
    ['post', /^\/live-event-task-lists\/([^/]+)\/preview$/, (db, m, { data }) => {
        const list = db.taskLists.find((l) => l.id === m[1]);
        if (!list) throw notFound('Task list');
        const run = { id: `pg-run-${list.id}`, ...mockTestRun(list, data || {}) };
        db.testRuns = { ...db.testRuns, [list.id]: run };
        return { liveEventTestRun: run };
    }],
    ['get', /^\/live-event-task-lists\/([^/]+)\/preview$/, (db, m) => {
        const run = db.testRuns?.[m[1]];
        if (!run) throw notFound('Test run');
        return { liveEventTestRun: run };
    }],
    ['get', /^\/game-knowledge-snippets$/, (db, m, { params }) => ({
        gameKnowledgeSnippets: db.snippets.filter((s) => inSystem(s, params)),
    })],
    ['post', /^\/game-knowledge-snippets$/, (db, m, { data }) => {
        // As the server stores a new one: it always says where it belongs and what it is.
        const row = {
            id: nextId(db, 'snip'), createdAt: iso(Date.now()), suggestedAction: 'NONE', ...data,
            system: data?.system || 'GAME_KNOWLEDGE', systemId: data?.systemId || null, type: data?.type || 'KNOWLEDGE_SNIPPET',
        };
        db.snippets.push(row);
        return { gameKnowledgeSnippet: row };
    }],
    ['put', /^\/game-knowledge-snippets\/([^/]+)$/, (db, m, { data }) => {
        const row = db.snippets.find((s) => s.id === m[1]);
        if (!row) throw notFound('Snippet');
        const { clearRule, ...rest } = data || {};
        if (clearRule) row.rule = null;
        return { gameKnowledgeSnippet: patch(row, clearRule ? { ...rest, rule: undefined } : rest) };
    }],
    ['delete', /^\/game-knowledge-snippets\/([^/]+)$/, (db, m) => {
        db.snippets = db.snippets.filter((s) => s.id !== m[1]);
        return { success: true };
    }],
    ['get', /^\/upcoming-live-events$/, (db, m, { params }) => upcoming(db, Number(params?.horizonDays) || 90)],
    ['get', /^\/timeline-items$/, (db) => ({ timelineItems: db.placements })],
    ['delete', /^\/timeline-items\/([^/]+)$/, (db, m) => {
        db.placements = db.placements.filter((p) => p.id !== m[1]);
        return { success: true };
    }],
];

export const playgroundRequest = async ({ method, path, data, params, form }) => {
    await new Promise((resolve) => { setTimeout(resolve, 150); });
    const db = load();
    const route = ROUTES.find(([verb, re]) => verb === method && re.test(path));
    if (!route) throw notFound(path);
    const result = route[2](db, path.match(route[1]), { data, params, form });
    save(db);
    return JSON.parse(JSON.stringify(result));
};

export const playgroundFileUrl = (id) => blobs.get(id) || null;

export const playgroundConfigEdit = async (message, text) => {
    await new Promise((resolve) => { setTimeout(resolve, 400); });
    return proposeConfigEdit(message, text);
};

// The knowledge chat without intel: the turn is drafted from the message, and steps go to the event's active list.
export const playgroundKnowledgeChat = async (message, event) => {
    await new Promise((resolve) => { setTimeout(resolve, 400); });
    const active = load().taskLists.find((l) => l.beatId === event.id && l.isDefault);
    return draftKnowledgeTurn(message, event, active ? { id: active.id, name: active.name } : null);
};
